import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge } from '../shared/ui/StatusBadge';

export function EmployeesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore(
    (s) => s.hasPermission('users:read') || s.hasPermission('maintenance:read'),
  );
  const canWrite = useAuthStore(
    (s) => s.hasPermission('users:write') || s.hasPermission('maintenance:write'),
  );
  const list = useQuery({
    queryKey: ['employees'],
    queryFn: () => api.listEmployees(),
    enabled: canRead,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: '',
    firstName: '',
    lastName: '',
    roleTitle: '',
    hiredAt: new Date().toISOString().slice(0, 10),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createEmployee({
        code: form.code,
        firstName: form.firstName,
        lastName: form.lastName,
        roleTitle: form.roleTitle,
        hiredAt: new Date(form.hiredAt).toISOString(),
      }),
    onSuccess: async () => {
      setOpen(false);
      await qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEmployee(id),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('employees.forbidden')}</div>;
  }

  const rows = list.data?.data ?? [];
  const columns = [
    t('employees.code'),
    t('employees.name'),
    t('employees.role'),
    t('employees.active'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
              {t('employees.create')}
            </button>
          ) : undefined
        }
      />

      <Card>
        <ParkingDataTable
          storageKey="employees"
          columns={columns}
          refreshKey={rows.map((e) => e.id).join('|')}
          actionsColumn={canWrite}
          galleryItems={rows.map((e) => ({
            id: e.id,
            icon: 'bi-person-badge',
            iconTone: e.isActive ? ('success' as const) : ('secondary' as const),
            title: e.code,
            subtitle: `${e.firstName} ${e.lastName}`,
            meta: e.roleTitle,
            badge: (
              <StatusBadge tone={e.isActive ? 'success' : 'secondary'}>
                {e.isActive ? t('common.activeOn') : t('common.activeOff')}
              </StatusBadge>
            ),
            actions: canWrite ? (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-ghost-danger"
                onClick={() => remove.mutate(e.id)}
              >
                <i className="bi bi-trash" />
              </button>
            ) : undefined,
          }))}
        >
          {rows.map((e) => (
            <tr key={e.id}>
              <td className="fw-semibold">{e.code}</td>
              <td>
                {e.firstName} {e.lastName}
              </td>
              <td>{e.roleTitle}</td>
              <td>
                <StatusBadge tone={e.isActive ? 'success' : 'secondary'}>
                  {e.isActive ? t('common.activeOn') : t('common.activeOff')}
                </StatusBadge>
              </td>
              {canWrite && (
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    onClick={() => remove.mutate(e.id)}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('employees.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!form.code || !form.firstName || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          <div>
            <label className="form-label">{t('employees.code')}</label>
            <input
              className="form-control"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('employees.firstName')}</label>
            <input
              className="form-control"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('employees.lastName')}</label>
            <input
              className="form-control"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('employees.role')}</label>
            <input
              className="form-control"
              value={form.roleTitle}
              onChange={(e) => setForm({ ...form, roleTitle: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('employees.hiredAt')}</label>
            <input
              className="form-control"
              type="date"
              value={form.hiredAt}
              onChange={(e) => setForm({ ...form, hiredAt: e.target.value })}
            />
          </div>
        </div>
      </AppModal>
    </>
  );
}
